import { Message } from 'ai';
import { useChat } from '@ai-sdk/react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toastUserError } from '@/lib/errors/toast-user-error';
import { ChatTiming } from '@/lib/timing/chat-timing';
import {
  ClientLatencyRecorder,
  readTraceTicket,
} from '@/lib/telemetry/latency/client';
import { useChatAttachments } from '@/hooks/use-chat-attachments';
import { useChatVoice } from '@/hooks/use-chat-voice';
import { useChatThreadSync } from '@/hooks/use-chat-thread-sync';
import {
  parseMessageReactionMode,
  resolveThreadReactions,
} from '@/lib/chat/message-reaction';

export interface ChatLogicProps {
  chatbotId: string;
  currentThreadId: string;
  initialMessage?: string;
  /** Restore a previous conversation (e.g. public pages localStorage history). Read on mount only. */
  initialMessages?: Message[];
  pendingMessage?: string | null;
  onPendingMessageSent?: () => void;
  initialMode?: 'text' | 'voice';
  messageReactionMode?: string;
}

type UploadedFileUIPart = {
  type: 'file';
  filename: string;
  mediaType: string;
  url: string;
  id?: string;
  fileId?: string;
  storageUrl?: string;
  storageProvider?: string;
};

export interface ChatLogicReturn {
  // State
  messages: Message[];
  input: string;
  setInput: (input: string) => void;
  isLoading: boolean;
  currentMode: 'text' | 'voice';
  status: string;
  error: Error | null;
  
  // Actions
  handleSubmit: (input: string, attachments: any[]) => Promise<void>;
  handleModeChange: (mode: 'text' | 'voice') => void;
  reload: () => void;
  stop: () => void;
  
  // Attachments
  attachments: any[];
  setAttachments: (attachments: any[]) => void;
  messageAttachments: Record<string, any[]>;
  handleAttachmentAdd: (attachment: any) => void;
  handleAttachmentRemove: (attachmentId: string) => void;
  
  // Voice
  voiceInterfaceRef: React.RefObject<any>;
  handleTranscriptReceived: (transcript: string, isFinal: boolean, segmentId?: string) => void;
  handleAgentResponseReceived: (response: string, segmentId?: string) => void;
  
  // Metadata
  threadId: string;
  messagesWithIds: Message[];
  combinedStatus: 'ready' | 'submitted' | 'streaming' | 'error';
  setMessages: (updater: Message[] | ((prev: Message[]) => Message[])) => void;
}

export function useChatLogic({
  chatbotId,
  currentThreadId,
  initialMessage,
  initialMessages,
  pendingMessage,
  onPendingMessageSent,
  initialMode = 'text',
  messageReactionMode,
}: ChatLogicProps): ChatLogicReturn {
  // State
  const [currentMode, setCurrentMode] = useState<'text' | 'voice'>(initialMode);
  const [input, setInput] = useState('');
  
  // Refs
  const lastResponseRef = useRef<string | null>(null);
  const voiceInterfaceRef = useRef<any>(null);
  const timingRef = useRef<ChatTiming | null>(null);
  /**
   * Phase 0 perceived-TTFT recorder. Its origin is the submit click, and every
   * value it reports is an offset from that single performance.now() reading —
   * it never touches a server clock. Observation only.
   */
  const latencyRef = useRef<ClientLatencyRecorder | null>(null);
  const firstTokenReceivedRef = useRef<boolean>(false);
  const hasAutoSentRef = useRef(false);
  const limitToastRef = useRef(false);
  
  // Update mode when initialMode prop changes (e.g., from window shell header toggle)
  useEffect(() => {
    if (initialMode && initialMode !== currentMode) {
      setCurrentMode(initialMode);
    }
  }, [initialMode]);
  
  // Set initial message if provided (e.g., from floating input)
  useEffect(() => {
    if (initialMessage && initialMessage.trim() && input === '') {
      setInput(initialMessage);
    }
  }, [initialMessage, input]);

  // Custom hooks
  const attachmentsHook = useChatAttachments();

  // AI SDK v6: Create transport for API communication
  const transport = useMemo(() => {
    const { DefaultChatTransport } = require('ai');
    
    return new DefaultChatTransport({
      api: `/api/chat?chatbotId=${encodeURIComponent(chatbotId)}&threadId=${encodeURIComponent(currentThreadId)}`,
      headers: () => {
        const headers: Record<string, string> = {
          'x-embed': '1',
          'x-chatbot-id': chatbotId,
        };
        
        // Pass timing context if available
        if (timingRef.current) {
          headers['x-chat-timing-id'] = timingRef.current.getRequestId();
          headers['x-chat-timing-data'] = timingRef.current.serialize();
        }
        
        return headers;
      },
      prepareSendMessagesRequest({ messages, id }) {
        const message = messages[messages.length - 1] as any;
        const fileParts = Array.isArray(message?.parts)
          ? message.parts.filter((part: any) => part.type === 'file')
          : [];
        const requestMessage =
          fileParts.length > 0
            ? {
                ...message,
                // The app runtime consumes image parts plus its legacy attachment
                // metadata. Convert UI file parts at the transport boundary so the
                // server can refresh private URLs and preserve Agent Vision context.
                parts: message.parts.filter((part: any) => part.type !== 'file'),
                attachments: fileParts.map((part: any) => ({
                  id: part.id || part.fileId || part.url,
                  fileId: part.fileId || part.id,
                  type: part.mediaType?.startsWith('image/') ? 'image' : 'file',
                  url: part.url,
                  name: part.filename,
                  contentType: part.mediaType,
                  storageUrl: part.storageUrl,
                  storageProvider: part.storageProvider,
                })),
              }
            : message;

        const reactionMode = parseMessageReactionMode(messageReactionMode);
        const userItems = messages
          .filter((item: any) => item?.role === 'user' && item?.id)
          .map((item: any) => {
            const parts = Array.isArray(item.parts)
              ? item.parts.filter((part: any) => part?.type === 'text').map((part: any) => part.text || '')
              : [];
            const text =
              parts.join(' ').trim() ||
              (typeof item.content === 'string' ? item.content : '');
            return { id: String(item.id), text };
          });
        const lastId = requestMessage?.id ? String(requestMessage.id) : '';
        const reactionEmoji = lastId
          ? resolveThreadReactions(userItems, reactionMode).get(lastId) ?? null
          : null;

        return { 
          body: { 
            message: {
              ...requestMessage,
              metadata: {
                ...(requestMessage?.metadata && typeof requestMessage.metadata === 'object'
                  ? requestMessage.metadata
                  : {}),
                ...(reactionEmoji ? { reactionEmoji } : {}),
              },
            },
            id,
            chatbotId,
            threadId: currentThreadId
          } 
        };
      },
      // Wraps the response so we can read the signed trace ticket and mark the
      // genuine first byte off the wire. Marking on headers instead would fold
      // in the server's prime-ping flush; this is the real transport moment.
      fetch: async (input: any, init: any) => {
        const response = await fetch(input, init);
        try {
          const rec = latencyRef.current;
          if (rec) {
            rec.setTicket(readTraceTicket(response));
            if (response.body) {
              const reader = response.body.getReader();
              const observed = new ReadableStream({
                async start(controller) {
                  try {
                    while (true) {
                      const { value, done } = await reader.read();
                      if (done) { controller.close(); return; }
                      rec.markFirstChunk();
                      controller.enqueue(value);
                    }
                  } catch (err) {
                    controller.error(err);
                  }
                },
                cancel(reason) { try { reader.cancel(reason); } catch {} },
              });
              return new Response(observed, {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
              });
            }
          }
        } catch {
          /* telemetry must never break the transport */
        }
        return response;
      },
    });
  }, [chatbotId, currentThreadId, messageReactionMode]);

  // AI SDK v6 Chat Integration
  const chatHelpers = useChat({
    id: currentThreadId,
    transport,
    ...(initialMessages && initialMessages.length > 0
      ? { messages: initialMessages }
      : {}),
    experimental_throttle: 0,
    onResponse: async (response) => {
      const contentType = response.headers.get('content-type') || '';
      if (response.status === 402 || response.status === 403) {
        limitToastRef.current = true;
        try {
          const data = await response.json();
          toastUserError(
            { ...data, status: response.status },
            response.status === 402
              ? "You've reached your message limit."
              : 'This agent is currently unavailable.',
          );
        } catch {
          toastUserError(
            response.status === 402
              ? { status: 402, error: 'usage_limit_reached', usageType: 'message' }
              : { status: 403, code: 'trial_expired' },
            "You've reached your message limit.",
          );
        }
        return;
      }
      limitToastRef.current = false;
      if (response.status !== 200) {
        console.error('❌ [Chat] Response error:', {
          status: response.status,
          statusText: response.statusText
        });
        toastUserError(
          { status: response.status },
          'Error receiving response from AI',
        );
      } else if (contentType.includes('text/html')) {
        console.error('❌ [Chat] Received HTML instead of stream');
        toastUserError(null, 'Error receiving response from AI');
      }
    },
    onFinish: (message) => {
      // Mark stream complete and log timing summary
      latencyRef.current?.complete();
      latencyRef.current = null;
      if (timingRef.current) {
        timingRef.current.mark('stream_complete');
        timingRef.current.logSummary();
        timingRef.current = null;
      }
      
      // AI SDK v6: Extract text from message.parts
      const textParts = (message as any)?.parts?.filter((p: any) => p.type === 'text') || [];
      const textContent = textParts.length > 0 
        ? textParts.map((p: any) => p.text).join('')
        : message?.content || '';
      
      lastResponseRef.current = textContent;
    },
    onError: (error) => {
      // Mark error in timing if available
      latencyRef.current?.complete();
      latencyRef.current = null;
      if (timingRef.current) {
        timingRef.current.mark('error_occurred');
        timingRef.current.logSummary();
        timingRef.current = null;
      }
      
      if (limitToastRef.current) {
        limitToastRef.current = false;
        return;
      }
      const raw = error instanceof Response
        ? { status: error.status, message: error.statusText }
        : error;
      toastUserError(raw, 'Error receiving message from AI');
    }
  });

  // Extract helpers from useChat
  const {
    messages = [],
    sendMessage,
    reload,
    regenerate,
    stop,
    status,
    error,
    setMessages,
  } = chatHelpers as any;
  
  // Use regenerate if available (AI SDK v6), otherwise fall back to reload
  const reloadOrRegenerate = regenerate || reload;
  
  // Auto-send initial message when provided
  useEffect(() => {
    if (initialMessage && initialMessage.trim() && sendMessage && !hasAutoSentRef.current) {
      const timer = setTimeout(() => {
        sendMessage({ text: initialMessage.trim() });
        setInput('');
        hasAutoSentRef.current = true;
      }, 200);
      return () => clearTimeout(timer);
    }
    if (!initialMessage) {
      hasAutoSentRef.current = false;
    }
  }, [initialMessage, sendMessage, setInput]);
  
  // A user who gives up on a slow response and closes the tab is exactly the
  // trace worth keeping, and it is the one a normal fetch would lose. pagehide
  // fires in that path; the recorder uses sendBeacon so it survives unload.
  useEffect(() => {
    const onHide = () => latencyRef.current?.flush();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      latencyRef.current?.flush();
    };
  }, []);

  // Track first token received.
  //
  // This effect runs AFTER React commits, so marking here measures content
  // actually painted rather than content received. That distinction is the
  // whole point of client_first_answer_render — marking in the stream handler
  // would silently report transport latency instead.
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== 'assistant') return;

    const parts = (lastMessage as any).parts || [];

    // Reasoning paints before the answer on thinking models; keep them apart.
    const reasoningText = parts
      .filter((p: any) => p.type === 'reasoning')
      .map((p: any) => p.text ?? p.reasoning ?? '')
      .join('');
    if (reasoningText.trim().length > 0) {
      latencyRef.current?.markFirstThoughtRender();
    }

    const textParts = parts.filter((p: any) => p.type === 'text');
    const textContent = textParts.length > 0
      ? textParts.map((p: any) => p.text).join('')
      : lastMessage.content || '';

    if (textContent.length > 0) {
      latencyRef.current?.markFirstAnswerRender();
      if (timingRef.current && !firstTokenReceivedRef.current) {
        timingRef.current.mark('first_token_received');
        firstTokenReceivedRef.current = true;
      }
    }
  }, [messages]);

  // Handle message submission
  const handleSubmit = useCallback(async (input: string, attachments: any[]) => {
    if (!input.trim() && attachments.length === 0) return;

    try {
      timingRef.current = new ChatTiming();
      // Origin = this instant. Everything the client reports is an offset
      // from here, on one clock.
      latencyRef.current = new ClientLatencyRecorder();
      firstTokenReceivedRef.current = false;
      timingRef.current.mark('message_submit_start');
      timingRef.current.mark('api_request_sent');

      const files: UploadedFileUIPart[] = attachments.map((attachment: any) => ({
        type: 'file',
        filename: attachment.name || attachment.filename || 'Attachment',
        mediaType:
          attachment.contentType || attachment.mimeType || 'application/octet-stream',
        url: attachment.url,
        // Preserve upload metadata through the UI message. The transport
        // converts these fields back to server attachment metadata.
        id: attachment.id,
        fileId: attachment.fileId || attachment.id,
        storageUrl: attachment.storageUrl,
        storageProvider: attachment.storageProvider,
      }));

      if (files.length > 0) {
        attachmentsHook.setAttachments([]);
      }

      await sendMessage({
        text: input.trim(),
        ...(files.length > 0 ? { files } : {}),
      });
    } catch (error) {
      console.error('❌ [Chat] useChat sendMessage error:', error);
      throw error;
    }
  }, [sendMessage, attachmentsHook.setAttachments]);

  // Handle pending message from drawer shell
  useEffect(() => {
    if (pendingMessage && pendingMessage.trim() && status === 'ready' && messages.length >= 0 && handleSubmit) {
      const messageToSend = pendingMessage;
      onPendingMessageSent?.();
      
      handleSubmit(messageToSend, []).catch((error) => {
        console.error('Failed to send pending message:', error);
      });
    }
  }, [pendingMessage, status, handleSubmit, onPendingMessageSent, messages.length]);

  // Voice hook - display only. Voice turns must not be sent to /api/chat; the
  // voice agent already owns the conversation and its tools.
  const { handleTranscriptReceived, handleAgentResponseReceived } = useChatVoice(setMessages);

  // Thread sync hook
  useChatThreadSync(currentThreadId, chatbotId, setMessages, status, error);

  // Ensure all messages have IDs
  const messagesWithIds = useMemo(() => {
    return messages.map(msg => ({
      ...msg,
      id: msg.id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    }));
  }, [messages]);

  const messageAttachments = useMemo(() => {
    const merged = { ...attachmentsHook.messageAttachments };
    for (const message of messagesWithIds) {
      const fileParts = Array.isArray((message as any).parts)
        ? (message as any).parts.filter((part: any) => part.type === 'file')
        : [];
      if (fileParts.length === 0) continue;
      merged[message.id] = fileParts.map((part: any) => ({
        id: part.id || part.fileId || part.url,
        fileId: part.fileId || part.id,
        type: part.mediaType?.startsWith('image/') ? 'image' : 'file',
        url: part.url,
        name: part.filename,
        contentType: part.mediaType,
        storageUrl: part.storageUrl,
        storageProvider: part.storageProvider,
      }));
    }
    return merged;
  }, [attachmentsHook.messageAttachments, messagesWithIds]);

  // Combined status
  const combinedStatus = status === 'streaming' ? 'streaming' :
                         status === 'submitted' ? 'submitted' :
                         error ? 'error' : 'ready';

  // Mode change handler
  const handleModeChange = useCallback((mode: 'text' | 'voice') => {
    if (mode !== currentMode) {
      if (mode === 'voice') {
        setInput('');
      }
    }
    setCurrentMode(mode);
  }, [currentMode]);

  return {
    // State
    messages: messagesWithIds,
    input,
    setInput,
    isLoading: status === 'streaming' || status === 'submitted',
    currentMode,
    status,
    error,
    setMessages, // Expose setMessages for components that need it
    
    // Actions
    handleSubmit,
    handleModeChange,
    reload: reloadOrRegenerate,
    stop,
    
    // Attachments
    attachments: attachmentsHook.attachments,
    setAttachments: attachmentsHook.setAttachments,
    messageAttachments,
    handleAttachmentAdd: attachmentsHook.addAttachment,
    handleAttachmentRemove: attachmentsHook.removeAttachment,
    
    // Voice
    voiceInterfaceRef,
    handleTranscriptReceived,
    handleAgentResponseReceived,
    
    // Metadata
    threadId: currentThreadId,
    messagesWithIds,
    combinedStatus,
  };
}
